import { useRef, useCallback, useSyncExternalStore, useEffect } from 'react';
import {
  requestTtsAudio,
  streamVoiceSearch,
  startVoiceStreamFetch,
  consumeVoiceStream,
} from '../services/chat-api.service';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { useUserContext } from '../context/use-user-context';
import { useCreateEvent } from './useCreateEvent';
import {
  VoiceModeOrchestrator,
  type VoiceModeState,
} from '../services/voice-mode-orchestrator';
import type { AudioAnalyserService } from '../services/audio-analyser.service';
import type {
  Citation,
  DisplayFilterGroup,
  SuggestedCTA,
} from '../services/types';

export type { VoiceModeState };

interface UseVoiceModeOptions {
  sendChatMessage: (args: {
    newMessage?: string;
    filterGroups?: DisplayFilterGroup[];
  }) => Promise<{ answer?: string } | undefined>;
  displayVoiceExchange: (transcript: string, answer: string) => void;
  updateVoiceAnswer: (answer: string) => void;
  updateVoiceMetadata: (metadata: {
    citations?: Citation[];
    suggestedCTA?: SuggestedCTA | null;
  }) => void;
  appliedFilterGroups?: DisplayFilterGroup[];
  startListening: (opts?: { continuous?: boolean }) => void;
  stopListening: () => void;
  trackEvent: (event: string, data?: Record<string, unknown>) => void;
}

export interface UseVoiceModeReturn {
  voiceModeState: VoiceModeState;
  isTtsMuted: boolean;
  isReadingOut: boolean;
  toggleVoiceMode: () => void;
  toggleTtsMute: () => void;
  exitVoiceMode: (
    reason?: 'toggle' | 'typed' | 'error' | 'silence' | 'max-turns'
  ) => void;
  stopAllAudio: () => void;
  handleSpeechEnd: (transcript: string) => void;
  handleSpeculativeSilence: (transcript: string) => void;
  handleSpeechResumed: () => void;
  isVoiceModeAvailable: boolean;
  analyserService: AudioAnalyserService;
}

export function useVoiceMode({
  sendChatMessage,
  displayVoiceExchange,
  updateVoiceAnswer,
  updateVoiceMetadata,
  appliedFilterGroups = [],
  startListening,
  stopListening,
  trackEvent,
}: UseVoiceModeOptions): UseVoiceModeReturn {
  const store = useStoreInfoStore((s) => s.store);
  const session = useStoreInfoStore((s) => s.currentSession);
  const { voiceResponsesOverride } = useUserContext();
  const { createEvent } = useCreateEvent();

  const storeHasVoiceResponses =
    store?.config?.config?.moduleEnabled?.includes('voice-responses') ?? false;
  const isTtsEnabled = voiceResponsesOverride ?? storeHasVoiceResponses;

  const isVoiceModeAvailable = isTtsEnabled;

  // Store volatile callback dependencies in refs so the orchestrator
  // always calls the latest versions without needing a new object each render.
  const sttRef = useRef({ startListening, stopListening });
  sttRef.current = { startListening, stopListening };

  const chatRef = useRef({
    sendChatMessage,
    displayVoiceExchange,
    updateVoiceAnswer,
    updateVoiceMetadata,
    getAppliedFilterGroups: () => appliedFilterGroups,
    streamVoiceSearch: (question: string) => {
      if (!store || !session) {
        throw new Error('Store and session required for voice stream');
      }
      return streamVoiceSearch({ question, session, store });
    },
    startSpeculativeFetch: (question: string, signal: AbortSignal) => {
      if (!store || !session) {
        return Promise.reject(
          new Error('Store and session required for voice stream')
        );
      }
      return startVoiceStreamFetch({ question, session, store, signal });
    },
    consumeVoiceStream: (response: Response) => consumeVoiceStream(response),
  });
  chatRef.current = {
    sendChatMessage,
    displayVoiceExchange,
    updateVoiceAnswer,
    updateVoiceMetadata,
    getAppliedFilterGroups: () => appliedFilterGroups,
    streamVoiceSearch: (question: string) => {
      if (!store || !session) {
        throw new Error('Store and session required for voice stream');
      }
      return streamVoiceSearch({ question, session, store });
    },
    startSpeculativeFetch: (question: string, signal: AbortSignal) => {
      if (!store || !session) {
        return Promise.reject(
          new Error('Store and session required for voice stream')
        );
      }
      return startVoiceStreamFetch({ question, session, store, signal });
    },
    consumeVoiceStream: (response: Response) => consumeVoiceStream(response),
  };

  const ttsRef = useRef({
    requestAudio: (text: string) => {
      if (!store || !session) {
        return Promise.reject(new Error('Store and session required for TTS'));
      }
      return requestTtsAudio({ text, session, store });
    },
    isEnabled: isTtsEnabled,
  });
  ttsRef.current = {
    requestAudio: (text: string) => {
      if (!store || !session) {
        return Promise.reject(new Error('Store and session required for TTS'));
      }
      return requestTtsAudio({ text, session, store });
    },
    isEnabled: isTtsEnabled,
  };

  const analyticsRef = useRef({ trackEvent, createEvent });
  analyticsRef.current = { trackEvent, createEvent };

  const orchestratorRef = useRef<VoiceModeOrchestrator | null>(null);
  if (!orchestratorRef.current) {
    orchestratorRef.current = new VoiceModeOrchestrator();
    orchestratorRef.current.updateCallbacks({
      stt: {
        startListening: (...args) => sttRef.current.startListening(...args),
        stopListening: () => sttRef.current.stopListening(),
      },
      chat: {
        sendMessage: (...args) => chatRef.current.sendChatMessage(...args),
        getAppliedFilterGroups: () => chatRef.current.getAppliedFilterGroups(),
        streamVoiceSearch: (question) =>
          chatRef.current.streamVoiceSearch(question),
        startSpeculativeFetch: (question, signal) =>
          chatRef.current.startSpeculativeFetch(question, signal),
        consumeVoiceStream: (response) =>
          chatRef.current.consumeVoiceStream(response),
        displayVoiceExchange: (transcript, answer) =>
          chatRef.current.displayVoiceExchange(transcript, answer),
        updateVoiceAnswer: (answer) =>
          chatRef.current.updateVoiceAnswer(answer),
        updateVoiceMetadata: (metadata) =>
          chatRef.current.updateVoiceMetadata(metadata),
      },
      tts: {
        requestAudio: (text) => ttsRef.current.requestAudio(text),
        get isEnabled() {
          return ttsRef.current.isEnabled;
        },
      },
      analytics: {
        trackEvent: (...args) => analyticsRef.current.trackEvent(...args),
        createEvent: (...args) => analyticsRef.current.createEvent(...args),
      },
    });
  }
  const orchestrator = orchestratorRef.current;

  const aud = useSyncExternalStore(
    (cb) => orchestrator.subscribe(cb),
    () => orchestrator.getSnapshot()
  );

  const { voiceModeState, isTtsMuted, isReadingOut } = aud;

  useEffect(() => {
    // Re-subscribe after StrictMode's simulated unmount calls dispose()
    orchestratorRef.current?.reconnectAudio();
    return () => orchestratorRef.current?.dispose();
  }, []);

  const toggleVoiceMode = useCallback(() => {
    if (!isVoiceModeAvailable) return;
    orchestrator.toggle();
  }, [isVoiceModeAvailable, orchestrator]);

  return {
    voiceModeState,
    isTtsMuted,
    isReadingOut,
    toggleVoiceMode,
    toggleTtsMute: () => orchestrator.toggleTtsMute(),
    exitVoiceMode: (reason) => orchestrator.exit(reason ?? 'toggle'),
    stopAllAudio: () => orchestrator.stopAllAudio(),
    handleSpeechEnd: (transcript: string) =>
      orchestrator.handleSpeechEnd(transcript),
    handleSpeculativeSilence: (transcript: string) =>
      orchestrator.handleSpeculativeSilence(transcript),
    handleSpeechResumed: () => orchestrator.handleSpeechResumed(),
    isVoiceModeAvailable,
    analyserService: orchestrator.getAnalyserService(),
  };
}
